import React from 'react';
import { useRef, useState } from 'react';

import {
  BookmarkCheck01Icon,
  Delete02Icon,
  DeliveryReturn02Icon,
  Edit03Icon,
} from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { ActionsMenu } from 'components/ActionsMenu/ActionsMenu';
import { CommentBox } from 'components/CommentBox/CommentBox';
import { IconComponent } from 'components/Icon/IconComponent';
import StatusPill from 'components/Pills/StatusPills';
import { UserInfo } from 'components/UserInfo/UserInfo';

export const Comments = ({
  title,
  caption,
  className,
  tooltip,
  initials,
  comment,
  tag,
  tagColor,
  tagValue,
  edited = false,
  tagOptions = [],
  allowDelete = false,
  allowEdit = false,
  onEdit = () => {},
  onDelete = () => {},
  allowResolve = false,
  onResolve = () => {},
  resolved = false,
  allowActions = true,
  onReopen = () => {},
}) => {
  const { t } = useTranslation();
  const actions = [
    {
      title: 'Edit',
      icon: Edit03Icon,
      onClick: () => handleEdit(),
      hide: !allowEdit,
      disabled: false,
      type: 'secondary',
    },
    {
      title: 'Reopen',
      icon: DeliveryReturn02Icon,
      onClick: () => onReopen(),
      hide: !allowResolve || !resolved,
      disabled: false,
      type: 'secondary',
    },
    {
      title: 'Delete',
      icon: Delete02Icon,
      onClick: () => onDelete(),
      hide: !allowDelete,
      disabled: false,
      type: 'error',
    },
  ];
  const [isEditing, setIsEditing] = useState(false);
  const commentBoxRef = useRef(null);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const updateSupportUserNote = ({ message, type }) => {
    onEdit({ message, type });
    setIsEditing(false);
  };

  return (
    <div className={cn('d-flex flex-column u-gap', { [className]: className })}>
      <div className="d-flex align-items-start justify-content-between">
        <div className="d-flex align-items-start u-gap-medium flex-wrap">
          <UserInfo
            title={title}
            caption={caption}
            initials={initials}
            tooltip={tooltip || title}
          />
          <div className="d-flex align-items-center u-gap">
            {tag && <StatusPill className="u-margin-right--small" text={tag} status={tagColor} />}
            {edited && <span className="u-font-size-x-small tertiary-text">Edited</span>}
          </div>
        </div>
        <div className="d-flex align-items-center u-gap-small">
          {allowResolve && resolved && (
            <IconComponent
              icon={BookmarkCheck01Icon}
              type="success"
              noBackground
              size="small"
              className="u-margin-right--small"
              tip="Resolved"
            />
          )}
          {allowResolve && !resolved && (
            <button
              className="u-font-size-x-small secondary-text u-border surface-white u-border-radius--x-small"
              onClick={onResolve}>
              Resolve
            </button>
          )}
          {allowActions && (
            <ActionsMenu newIconLibrary={true} small>
              {actions.map((action, index) =>
                action.hide ? null : (
                  <ActionsMenu.Item
                    newIconLibrary={true}
                    key={action.title + index}
                    icon={action.icon}
                    title={action.title}
                    onClick={() => action.onClick()}
                    disabled={action.disabled}
                    type={action.type}
                  />
                ),
              )}
            </ActionsMenu>
          )}
        </div>
      </div>
      {isEditing ? (
        <CommentBox
          ref={commentBoxRef}
          tags={tagOptions}
          tagDefault={tagValue}
          className="u-border-top"
          value={comment}
          onSubmit={({ message, tag }) => updateSupportUserNote({ message, type: tag })}
          onCancel={() => setIsEditing(false)}
          actionLabel={t('common.addNote')}
        />
      ) : (
        <span className="u-font-size-small secondary-text">{comment}</span>
      )}
    </div>
  );
};
